import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Moon, Clock, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import ImgFallback from "@/components/ImgFallback";

export default function AfterHoursRequest() {
  const { studioId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [studio, setStudio] = useState(null);
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [proposedTime, setProposedTime] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/studios/${studioId}`).then((r) => {
      setStudio(r.data);
      if (!serviceId) setServiceId(r.data.services?.[0]?.id || "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioId]);

  if (!user) {
    return (
      <section className="max-w-md mx-auto px-6 py-24 text-center" data-testid="afterhours-signin">
        <p className="overline mb-3">Sign in required</p>
        <h1 className="font-display text-3xl">Sign in to request an after-hours slot.</h1>
        <Link to="/auth" className="btn-gold inline-block mt-8 px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Sign in</Link>
      </section>
    );
  }

  if (!studio) return <div className="max-w-3xl mx-auto px-5 py-10 text-cream/50" data-testid="afterhours-loading">Loading…</div>;

  const submit = async (e) => {
    e.preventDefault();
    if (!proposedTime || !serviceId) { toast.error("Pick a service and time"); return; }
    setSubmitting(true);
    try {
      const iso = new Date(proposedTime).toISOString();
      const { data } = await api.post("/after_hours", {
        studio_id: studioId, service_id: serviceId, proposed_time: iso, notes,
      });
      toast.success("Request sent — waiting on the Professional.");
      nav("/after-hours/mine");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send request");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 pt-8 pb-24" data-testid="afterhours-page">
      <p className="overline mb-2 flex items-center gap-2 justify-start"><Moon size={12} weight="fill" color="#C5A059" /> After-hours request</p>
      <h1 className="font-display text-3xl md:text-4xl tracking-tighter text-cream">Propose a time to {studio.name}.</h1>
      <p className="text-cream/70 mt-2 text-sm">Their normal calendar is closed for this window. They'll <b>Accept</b>, <b>Counter</b>, or <b>Decline</b>. Nothing is charged until it becomes a confirmed booking.</p>

      <div className="mt-8 border border-cream/10 overflow-hidden">
        {studio.cover_image && (
          <ImgFallback src={studio.hero_look || studio.cover_image} alt={studio.name} className="w-full h-32 object-cover" />
        )}
        <div className="p-5 space-y-6">
          <div>
            <p className="overline mb-2">Service</p>
            <div className="space-y-2" data-testid="afterhours-services">
              {studio.services?.map((s) => (
                <button
                  key={s.id}
                  data-testid={`afterhours-service-${s.id}`}
                  onClick={() => setServiceId(s.id)}
                  className={`w-full text-left p-3 border ${serviceId === s.id ? "border-gold bg-gold/5" : "border-cream/10 hover:border-cream/30"}`}
                >
                  <p className="text-sm text-cream">{s.name}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-1">${s.price} · {s.duration_minutes} min</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="overline mb-2 flex items-center gap-2"><Clock size={12} weight="fill" color="#C5A059" /> Proposed time</p>
            <input
              type="datetime-local"
              data-testid="afterhours-time-input"
              value={proposedTime}
              onChange={(e) => setProposedTime(e.target.value)}
              className="w-full bg-transparent border border-cream/15 p-3 text-cream focus:border-gold focus:outline-none"
            />
          </div>

          <div>
            <p className="overline mb-2">Notes (optional)</p>
            <textarea
              data-testid="afterhours-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything they should know?"
              className="w-full bg-transparent border border-cream/15 p-3 text-cream focus:border-gold focus:outline-none text-sm"
            />
          </div>

          <button
            onClick={submit}
            disabled={submitting}
            data-testid="afterhours-submit"
            className="btn-gold w-full px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center justify-center gap-2"
          >
            {submitting ? "Sending…" : (<>Send request <ArrowRight size={14} /></>)}
          </button>
        </div>
      </div>
    </div>
  );
}
